const DRAGDROP_SIZE = 0.5;
const TIMEOUT_DURATION = 20;

export {TIMEOUT_DURATION};

export default class HeightCalculator {
    constructor() {
        /**
         * @type {MemorizedHeights}
         */
        this.memorizedHeights = {};
        /**
         * @type {number}
         */
        this.dragDropSize = DRAGDROP_SIZE;
    }

    /**
     * @param config {Config}
     */
    updateConfig(config) {
        this.config = config;
    }

    /**
     * @returns {"2cols"|"3cols"|"tiles"}
     */
    getType() {
        if (this.config.mode === 'tiles') return this.config.mode;
        return this.config.grid;
    }

    /**
     * @returns {boolean}
     */
    allHeightsMemorized() {
        if (!this.memorizedHeights.navigation) return false;
        if (!this.memorizedHeights.filter ) return false;
        if (!this.memorizedHeights.title) return false;

        return true;
    }

    /**
     * @param forceRecalculation {boolean}
     */
    memorizeHeights(forceRecalculation = false) {
        if (this.allHeightsMemorized() && !forceRecalculation) return;

        const INCLUDE_MARGIN = true;

        const navElement = $('#dragdrop-nav-tabs');
        const titleElement = $('#editor-settings .creator-title-text');
        const filterContainerElement = $('.dragdrop-filters-container');
        const filterElement = $('.dragdrop-filters-container .v-input');
        const creatorRowElement = $('#creator-row');
        const marginElement = $('.dragdrop-col');

        let filterHeight;
        filterElement.each((i, element) => {
            if (element.offsetHeight) filterHeight = element.offsetHeight;
        });

        let filterContainerElementHeight;
        filterContainerElement.each((i, element) => {
            if (element.offsetHeight) filterContainerElementHeight = element.offsetHeight;
        });

        if (!filterElement.length || !filterHeight) return;
        if (!filterContainerElement.length) return;
        if (!titleElement.length) return;
        if (!navElement.length) return;
        if (!marginElement.length) return;
        if (!creatorRowElement.length) return;

        const marginOuter = marginElement.outerHeight(INCLUDE_MARGIN);
        const marginInner = marginElement.height();

        this.memorizedHeights.margin = marginOuter - marginInner;
        this.memorizedHeights.navigation = navElement.outerHeight(INCLUDE_MARGIN);
        this.memorizedHeights.title = titleElement.height();
        this.memorizedHeights.filter = filterContainerElementHeight;
        this.memorizedHeights.total = Math.max(creatorRowElement.height(), 300);

        // console.log('Heights got memorized', JSON.stringify(this.memorizedHeights));
    }

    /**
     * @returns {MemorizedHeights}
     */
    getMemorizedHeights() {
        return this.memorizedHeights;
    }

    /**
     * @returns {Heights}
     */
    getHeights() {
        const INCLUDE_MARGIN = true;

        const memorizedHeights = this.getMemorizedHeights();

        const totalHeight = memorizedHeights.total ?? $('#creator-row').height();
        const titleHeight = memorizedHeights.title ?? $('#editor-settings .creator-title-text').height();

        const editorElement = $('.editor-col');
        const editorTotalHeight = editorElement.outerHeight(INCLUDE_MARGIN) ?? 0;
        const editorInnerHeight = editorElement.height() ?? 0;

        const marginHeight = memorizedHeights.margin ?? (editorTotalHeight - editorInnerHeight);

        return {
            margin: marginHeight,
            screen: totalHeight,
            editor: editorTotalHeight,
            title: titleHeight,
            dragdropNavTab: memorizedHeights.navigation,
            filter: this.config.tabName !== 'elements' ? memorizedHeights.filter : 0,
        }
    }

    /**
     * @param {('dragdrop'|'settings')} dragdropOrSettings
     * @returns {number}
     */
    execute(dragdropOrSettings) {
        const type = this.getType();

        const heights = this.getHeights();

        if (dragdropOrSettings === 'dragdrop') {
            // console.log('Current heights [DragDrop]', JSON.stringify(heights));
            if (type === 'tiles') {
                return heights.screen - heights.editor - heights.dragdropNavTab - heights.filter - heights.margin;
            }

            if (type === '3cols') {
                return heights.screen - heights.title - heights.margin;
            }

            if (type === '2cols') {
                if (this.config.collapsed) {
                    return heights.screen - (2 * heights.margin) - heights.dragdropNavTab - heights.filter - heights.title;
                }

                return (heights.screen * this.dragDropSize) - heights.filter - heights.margin;
            }
        }

        if (dragdropOrSettings === 'settings') {
            // console.log('Current heights [Settings]', JSON.stringify(heights));
            if (type === 'tiles') {
                return heights.screen - heights.title - heights.editor - heights.margin;
            }

            if (type === '3cols') {
                return heights.screen - heights.title - heights.margin;
            }

            if (type === '2cols') {
                if (this.config.fullSize) {
                    return heights.screen - heights.title;
                }

                if (this.config.collapsed) {
                    return heights.screen - 2 * (heights.title + heights.margin);
                }

                return heights.screen - (heights.screen * this.dragDropSize) - heights.title - heights.filter;
            }
        }
    }
}

/**
 * @typedef {Object} Config
 * @property {('tiles'|'list')} mode
 * @property {('2cols'|'3cols')} grid
 * @property {string} tabName
 * @property {boolean} collapsed
 * @property {boolean} fullSize
 */

/**
 * @typedef {Object} Heights
 * @property {number} filter
 * @property {number} editor
 * @property {number} margin
 * @property {number} dragdropNavTab
 * @property {number} screen
 * @property {number} title
 */

/**
 * @typedef {Object} MemorizedHeights
 * @property {number} navigation
 * @property {number} filter
 * @property {number} title
 * @property {number} total
 * @property {number} margin
 */
